import { useState } from "react";
import { Link } from "react-router-dom";
import { Card } from "@tremor/react";
import {
  Clock, ExternalLink, ChevronRight,
  ChevronDown, Database, Zap, Lock, Layers, Eye,
} from "lucide-react";
import {
  useCapabilities, useCapabilitySample,
  type CapabilityEndpoint, type VendorBlock,
} from "@/lib/queries";
import { cn } from "@/lib/utils";


export default function Capabilities() {
  const { data, isLoading } = useCapabilities();

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-semibold tracking-tight">Capabilities</h1>
        <p className="text-sm text-muted-foreground">
          Every external API endpoint Auriel can reach, what it returns, what
          it costs, and whether we've wired it. The shared workbench: walk
          through each vendor, see what's available vs what we're using, mark
          what's worth substrate-ing. Where the <Link to="/inspector" className="text-primary hover:underline">Inspector</Link> shows what we've fetched, this shows what's available to fetch.
        </p>
      </div>

      {isLoading && (
        <Card><div className="text-sm text-muted-foreground">Loading…</div></Card>
      )}

      {data && (
        <div className="space-y-4">
          {/* Top-level summary */}
          <SummaryStrip data={data} />

          {/* Vendor blocks */}
          <div className="space-y-4">
            {data.vendors.map((v) => <VendorCard key={v.vendor} vendor={v} />)}
          </div>
        </div>
      )}
    </div>
  );
}


function SummaryStrip({ data }: { data: { vendors: VendorBlock[]; catalog_total: number } }) {
  const totalSubstrate = data.vendors.reduce((s, v) => s + v.substrate_count, 0);
  const totalWired = data.vendors.reduce((s, v) => s + v.wired_count, 0);
  const totalNotWired = data.vendors.reduce((s, v) => s + v.not_wired_count, 0);
  return (
    <Card>
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
        <Stat label="Total endpoints" value={data.catalog_total} icon={Layers} />
        <Stat label="Substrate-backed" value={totalSubstrate} icon={Database} kind="ok" />
        <Stat label="Wired (no substrate)" value={totalWired - totalSubstrate} icon={Zap} kind="warn" />
        <Stat label="Not wired yet" value={totalNotWired} icon={Eye} kind="muted" />
      </div>
    </Card>
  );
}


function VendorCard({ vendor: v }: { vendor: VendorBlock }) {
  const [expanded, setExpanded] = useState(true);
  const kindColor = {
    free:     "bg-emerald-500/10 text-emerald-700 dark:text-emerald-400",
    paid:     "bg-amber-500/10 text-amber-700 dark:text-amber-400",
    internal: "bg-blue-500/10 text-blue-700 dark:text-blue-400",
  }[v.kind] ?? "bg-muted";

  return (
    <Card className="p-0 overflow-hidden">
      <button
        type="button"
        onClick={() => setExpanded((e) => !e)}
        className="flex w-full items-center justify-between gap-3 px-4 py-3 hover:bg-accent/40"
      >
        <div className="flex items-center gap-3">
          <ChevronDown className={cn("h-4 w-4 transition-transform", !expanded && "-rotate-90")} />
          <div className="text-left">
            <div className="flex items-center gap-2">
              <h2 className="text-sm font-semibold">{v.display}</h2>
              <span className={cn("rounded px-1.5 py-0.5 text-[10px] font-medium uppercase tracking-wider", kindColor)}>
                {v.kind}
              </span>
              <span className="text-[11px] text-muted-foreground">{v.auth}</span>
            </div>
            <div className="mt-0.5 flex items-center gap-3 text-[11px] text-muted-foreground">
              <span>{v.total_endpoints} endpoints</span>
              {v.substrate_count > 0 && (
                <span className="text-emerald-700 dark:text-emerald-400">
                  {v.substrate_count} substrate
                </span>
              )}
              <span className={v.wired_count > 0 ? "text-amber-700 dark:text-amber-400" : ""}>
                {v.wired_count - v.substrate_count} wired
              </span>
              {v.not_wired_count > 0 && (
                <span>{v.not_wired_count} not wired</span>
              )}
            </div>
          </div>
        </div>
      </button>
      {expanded && (
        <div className="border-t border-border">
          {v.endpoints.map((ep) => <EndpointRow key={ep.endpoint_name} endpoint={ep} />)}
        </div>
      )}
    </Card>
  );
}


function EndpointRow({ endpoint: ep }: { endpoint: CapabilityEndpoint }) {
  const [expanded, setExpanded] = useState(false);
  const [showSample, setShowSample] = useState(false);

  return (
    <div className="border-b border-border last:border-b-0">
      <button
        type="button"
        onClick={() => setExpanded((e) => !e)}
        className="flex w-full items-start gap-3 px-4 py-2.5 text-left hover:bg-accent/30"
      >
        <ChevronRight className={cn("mt-0.5 h-3.5 w-3.5 shrink-0 transition-transform", expanded && "rotate-90")} />
        <StatusBadge status={ep.auriel_status} />
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-baseline gap-2">
            <span className="font-medium">{ep.label}</span>
            <span className="font-mono text-[11px] text-muted-foreground">{ep.endpoint_name}</span>
            <span className="rounded bg-muted px-1.5 py-0.5 text-[10px] uppercase tracking-wider text-muted-foreground">
              {ep.category}
            </span>
          </div>
          {!expanded && (
            <div className="mt-0.5 line-clamp-1 text-xs text-muted-foreground">
              {ep.description}
            </div>
          )}
        </div>
        <div className="flex shrink-0 items-center gap-3 text-[11px] text-muted-foreground">
          {ep.calls_30d > 0 && (
            <span title={`${ep.calls_30d} calls in last 30d`}>
              <Clock className="mr-0.5 inline h-3 w-3" />
              {ep.calls_30d}
              {ep.errors_30d > 0 && (
                <span className="ml-0.5 text-red-600 dark:text-red-400">
                  · {ep.errors_30d} err
                </span>
              )}
            </span>
          )}
        </div>
      </button>

      {expanded && (
        <div className="space-y-3 bg-muted/20 px-4 py-3 pl-12">
          {/* Description */}
          <div className="text-xs">{ep.description}</div>

          {/* Value props */}
          {ep.value_props.length > 0 && (
            <div>
              <div className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
                Why it matters
              </div>
              <ul className="mt-1 space-y-0.5">
                {ep.value_props.map((vp, i) => (
                  <li key={i} className="text-xs">
                    <span className="text-muted-foreground">• </span>{vp}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Coverage gap */}
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <div>
              <div className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
                Currently
              </div>
              <div className={cn(
                "mt-0.5 text-xs",
                !ep.current_pull && "italic text-muted-foreground",
              )}>
                {ep.current_pull ?? "not pulled"}
              </div>
            </div>
            <div>
              <div className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
                Recommended
              </div>
              <div className="mt-0.5 text-xs">
                {ep.recommended_pull ?? "—"}
              </div>
            </div>
          </div>

          {/* Unlocks */}
          {ep.unlocks.length > 0 && (
            <div>
              <div className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
                Unlocks
              </div>
              <ul className="mt-1 space-y-0.5">
                {ep.unlocks.map((u, i) => (
                  <li key={i} className="font-mono text-[11px]">
                    <span className="text-muted-foreground">→ </span>{u}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Cost + docs */}
          <div className="flex flex-wrap items-center justify-between gap-2 text-[11px]">
            <div>
              <span className="text-muted-foreground">Cost:</span>{" "}
              <span className="font-mono">{ep.cost}</span>
            </div>
            <a
              href={ep.docs_url}
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center gap-1 text-primary hover:underline"
            >
              vendor docs <ExternalLink className="h-3 w-3" />
            </a>
          </div>

          {/* Sample row */}
          <div>
            <div className="flex items-center justify-between">
              <div className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
                Sample
              </div>
              {ep.calls_30d > 0 && (
                <button
                  type="button"
                  onClick={() => setShowSample((s) => !s)}
                  className="rounded-md border border-border bg-background px-2 py-0.5 text-[11px] hover:bg-accent"
                >
                  {showSample ? "Hide latest" : "Show latest"}
                </button>
              )}
            </div>
            {ep.calls_30d === 0 && (
              <div className="mt-1 text-[11px] italic text-muted-foreground">
                No samples — endpoint hasn't been called in the last 30 days.
              </div>
            )}
            {ep.calls_30d > 0 && !showSample && ep.last_called_at && (
              <div className="mt-1 text-[11px] text-muted-foreground">
                Last called {formatDateAgo(ep.last_called_at)} ago.{" "}
                {ep.latest_row_id !== null && (
                  <Link
                    to={`/inspector?row_id=${ep.latest_row_id}`}
                    className="text-primary hover:underline"
                  >
                    Open in Inspector →
                  </Link>
                )}
              </div>
            )}
            {showSample && <SamplePanel vendor={ep.vendor} endpoint={ep.endpoint_name} />}
          </div>
        </div>
      )}
    </div>
  );
}


function SamplePanel({ vendor, endpoint }: { vendor: string; endpoint: string }) {
  const sample = useCapabilitySample(vendor, endpoint);

  if (sample.isLoading) {
    return <div className="mt-2 text-[11px] text-muted-foreground">Loading sample…</div>;
  }
  if (!sample.data || !sample.data.available) {
    return <div className="mt-2 text-[11px] italic text-muted-foreground">No sample available.</div>;
  }
  const s = sample.data;
  return (
    <div className="mt-1 space-y-1">
      <div className="text-[10px] text-muted-foreground">
        Fetched {s.fetched_at && formatDateAgo(s.fetched_at)} ago · target=<span className="font-mono">{s.target}</span> · {s.response_size_bytes && formatBytes(s.response_size_bytes)}
      </div>
      {s.error && (
        <div className="rounded bg-red-500/10 px-2 py-1 font-mono text-[11px] text-red-700 dark:text-red-400">
          {s.error}
        </div>
      )}
      <pre className="max-h-[300px] overflow-auto rounded bg-background p-2 font-mono text-[10px] leading-relaxed">
        {s.response === null ? "(null)" : JSON.stringify(s.response, null, 2)}
      </pre>
    </div>
  );
}


function StatusBadge({ status }: { status: CapabilityEndpoint["auriel_status"] }) {
  const map = {
    wired_substrate: { label: "substrate", cls: "bg-emerald-500/15 text-emerald-700 dark:text-emerald-400", icon: Database },
    wired:           { label: "wired",     cls: "bg-amber-500/10 text-amber-700 dark:text-amber-400",       icon: Zap },
    not_wired:       { label: "not wired", cls: "bg-muted text-muted-foreground",                            icon: Eye },
    quota_blocked:   { label: "blocked",   cls: "bg-red-500/10 text-red-700 dark:text-red-400",             icon: Lock },
  } as const;
  const { label, cls, icon: Icon } = map[status];
  return (
    <span className={cn("inline-flex items-center gap-1 rounded px-1.5 py-0.5 text-[10px] font-medium uppercase tracking-wider", cls)}>
      <Icon className="h-2.5 w-2.5" />
      {label}
    </span>
  );
}


function Stat({ label, value, icon: Icon, kind }: {
  label: string; value: number;
  icon: typeof Database;
  kind?: "ok" | "warn" | "muted";
}) {
  return (
    <div className="rounded-md border border-border bg-muted/30 px-3 py-2">
      <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
        <Icon className="h-3.5 w-3.5" />
        {label}
      </div>
      <div className={cn(
        "mt-0.5 text-lg font-semibold",
        kind === "ok"   && "text-emerald-700 dark:text-emerald-400",
        kind === "warn" && "text-amber-700 dark:text-amber-400",
        kind === "muted" && "text-muted-foreground",
      )}>
        {value.toLocaleString()}
      </div>
    </div>
  );
}


function formatDateAgo(iso: string): string {
  const t = new Date(iso).getTime();
  const sec = Math.round((Date.now() - t) / 1000);
  if (sec < 60) return `${sec}s`;
  if (sec < 3600) return `${Math.round(sec / 60)}m`;
  if (sec < 86400) return `${Math.round(sec / 3600)}h`;
  return `${Math.round(sec / 86400)}d`;
}


function formatBytes(n: number): string {
  if (n < 1024) return `${n}B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)}KB`;
  return `${(n / 1024 / 1024).toFixed(1)}MB`;
}
